import _ from "lodash";
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  memo,
  useRef,
  useEffect,
} from "react";

import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import {
  getUserThirdPartyAuthorizedStatus,
  generateThirdPartyRefreshToken,
  getUserThirdPartyPickerTokens,
} from "modules/Services";
import {
  GOOGLE_CLIENT_ID,
  ZOOM_CLIENT_ID,
  CLEVER_CLIENT_ID,
  LEX_SSO_URL,
  CLASSLINK_SSO_URL,
  LEX_TODDLE_PILOT_SSO_URL,
} from "Constants";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import OauthPopup from "UIComponents/OauthPopup";
import ACLStore from "lib/aclStore";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  generateCodeChallenge,
  generateRandomString,
} from "Utils/StringHelpers";
import { setJsonItem } from "services/localStorage";
import {
  LEX_CODE_VERIFIER_KEY,
  LEX_PILOT_CODE_VERIFIER_KEY,
} from "Constants/stringConstants";

const redirectUri = encodeURI(window.location.origin);
const microsoftRedirectUri = `${redirectUri}/sso/microsoft`;
import { microsoftClientId } from "config";
import { isNewVideoMeetingUIFeatureEnabled } from "MyCalendar/components/EventModalV2/modules/Utils";

const GOOGLE_DRIVE_SCOPE = {
  SCOPE_STUDENT: `https%3A//www.googleapis.com/auth/drive`,
  SCOPE_STAFF: `https%3A//www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly`,
  SCOPE_STAFF_FILE_ONLY: `https%3A//www.googleapis.com/auth/drive.file`,
};

const SERVICES = {
  GOOGLE: {
    getAuthUrl: ({ userType } = {}) =>
      userType != "staff"
        ? `https://accounts.google.com/o/oauth2/v2/auth?scope=${GOOGLE_DRIVE_SCOPE.SCOPE_STUDENT} https%3A//www.googleapis.com/auth/userinfo.email
  &access_type=offline&response_type=code&redirect_uri=${redirectUri}&client_id=${GOOGLE_CLIENT_ID}&prompt=consent&cookiePolicy=single_host_origin`
        : `https://accounts.google.com/o/oauth2/v2/auth?scope=${
            ACLStore.can("FeatureFlag:UseGoogleFileScopeOnly")
              ? GOOGLE_DRIVE_SCOPE.SCOPE_STAFF_FILE_ONLY
              : GOOGLE_DRIVE_SCOPE.SCOPE_STAFF
          } https%3A//www.googleapis.com/auth/userinfo.email https%3A//www.googleapis.com/auth/calendar ${
            isNewVideoMeetingUIFeatureEnabled()
              ? "https://www.googleapis.com/auth/meetings.space.created"
              : ""
          } ${
            ACLStore.can("FeatureFlag:EnableGooglePhotosPicker")
              ? "https://www.googleapis.com/auth/photospicker.mediaitems.readonly"
              : ""
          }
  &access_type=offline&response_type=code&redirect_uri=${redirectUri}&client_id=${GOOGLE_CLIENT_ID}&prompt=consent&cookiePolicy=single_host_origin`,
    refreshToken: true,
  },
  MICROSOFT: {
    getAuthUrl: () =>
      `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${microsoftClientId}&response_type=code&redirect_uri=${microsoftRedirectUri}&scope=offline_access%20Files.ReadWrite.All%20Calendars.ReadWrite%20OnlineMeetings.ReadWrite%20Sites.ReadWrite.All%20ChannelSettings.Read.All%20User.Read`,
    refreshToken: true,
  },
  ZOOM: {
    getAuthUrl: () =>
      `https://zoom.us/oauth/authorize?client_id=${ZOOM_CLIENT_ID}&response_type=code&redirect_uri=${redirectUri}&scope=meeting:write+user:read`,
    refreshToken: true,
  },
  CLEVER: {
    getAuthUrl: () =>
      `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${redirectUri}&client_id=${CLEVER_CLIENT_ID}`,
    refreshToken: false,
  },
  LEX: {
    getAuthUrl: () => {
      const codeVerifier = generateRandomString(96);
      setJsonItem({ key: LEX_CODE_VERIFIER_KEY, value: codeVerifier });
      const codeChallenge = generateCodeChallenge(codeVerifier);

      return LEX_SSO_URL(codeChallenge);
    },
    refreshToken: false,
  },
  LEX_PILOT: {
    getAuthUrl: () => {
      const codeVerifier = generateRandomString(96);
      setJsonItem({ key: LEX_PILOT_CODE_VERIFIER_KEY, value: codeVerifier });
      const codeChallenge = generateCodeChallenge(codeVerifier);

      return LEX_TODDLE_PILOT_SSO_URL(codeChallenge);
    },
    refreshToken: false,
  },
  CLASSLINK: {
    getAuthUrl: () => CLASSLINK_SSO_URL,
    refreshToken: false,
  },
};

const ThirdPartyAuth = (props, ref) => {
  const {
    serviceType,
    onSuccessCallBack,
    getUserThirdPartyAuthorizedStatus,
    generateThirdPartyRefreshToken,
    userId,
    userType,
    getUserThirdPartyPickerTokens,
    fetchThirdPartyPickerTokens = false,
    onFailureCallBack,
    showChildren = false,
    setIsThirdPartyAuthLoading = _.noop,
  } = props;
  const authRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(isLoading);
  const accessTokenRef = useRef({});

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useImperativeHandle(ref, () => ({
    onAuthClick: async ({ forceAuthorized = false } = {}) => {
      //return if currently loading
      if (
        ACLStore.can("FeatureFlag:DisableMultiAuthWindowPopup") &&
        isLoadingRef.current
      ) {
        return;
      }
      // If a popup with the same service type is open and active, bring it into focus.
      if (
        ACLStore.can("FeatureFlag:DisableMultiAuthWindowPopup") &&
        !!authRef &&
        !!authRef.current &&
        !!authRef.current?.isPopupActive?.()
      ) {
        authRef.current.createPopup();
        return;
      }
      const { refreshToken } = _.get(SERVICES, serviceType, {});

      // Handles the flow specific to retrieving a token for the third-party account picker
      if (fetchThirdPartyPickerTokens && refreshToken) {
        setIsLoading(true);
        setIsThirdPartyAuthLoading(true);
        const { initiateAuth, data } = await getUserThirdPartyPickerTokens({
          serviceType,
        });
        setIsLoading(false);
        setIsThirdPartyAuthLoading(false);
        if (!initiateAuth && data && onSuccessCallBack) {
          onSuccessCallBack(data);
          return;
        }
        if (initiateAuth && !!authRef && !!authRef.current) {
          authRef.current.createPopup();
        }
        return;
      }

      if (refreshToken) {
        setIsLoading(true);
        setIsThirdPartyAuthLoading(true);
        const authorizeStatus = forceAuthorized
          ? false
          : await getUserThirdPartyAuthorizedStatus({
              serviceType,
            });
        setIsLoading(false);
        setIsThirdPartyAuthLoading(false);
        if (authorizeStatus) {
          const accessToken =
            _.get(authorizeStatus, "accessToken") ||
            _.get(authorizeStatus, "isConnected");
          const id = _.get(authorizeStatus, "id", "");
          if (accessToken) {
            if (onSuccessCallBack) {
              onSuccessCallBack({ accessToken, id });
            }
            return;
          }
        }
      }

      if (!!authRef && !!authRef.current) {
        authRef.current.createPopup();
      }
    },
  }));

  const onAuthCallBack = async accessCode => {
    const { refreshToken } = _.get(SERVICES, serviceType, {});
    if (refreshToken) {
      setIsLoading(true);
      setIsThirdPartyAuthLoading(true);
      const accessTokenRefKey = `${userId}_${userType}_${serviceType}_${accessCode}`;
      const accessTokenValue = _.get(
        accessTokenRef,
        ["current", accessTokenRefKey],
        ""
      );

      let accessToken, id;
      if (_.isEmpty(accessTokenValue)) {
        const response = await generateThirdPartyRefreshToken({
          accessCode,
          serviceType,
          redirectUri: _.isEqual(serviceType, "MICROSOFT")
            ? microsoftRedirectUri
            : redirectUri,
        });
        const accessTokenResponse = _.get(response, "accessToken");
        accessToken = accessTokenResponse;
        id = _.get(response, "id", "");
        if (!_.isEmpty(accessToken)) {
          accessTokenRef.current[accessTokenRefKey] = accessToken;
        }
      } else {
        accessToken = accessTokenValue;
      }

      // Handles authentication flow specific to third-party account popups
      if (fetchThirdPartyPickerTokens) {
        const { data } = await getUserThirdPartyPickerTokens({
          serviceType,
        });
        setIsLoading(false);
        setIsThirdPartyAuthLoading(false);
        if (data && onSuccessCallBack) {
          onSuccessCallBack(data);
        }
        return;
      }

      setIsLoading(false);
      setIsThirdPartyAuthLoading(false);
      if (accessToken && onSuccessCallBack) {
        onSuccessCallBack({ accessToken, id });
      }
    } else {
      onSuccessCallBack({
        accessCode,
        redirectUri: _.isEqual(serviceType, "MICROSOFT")
          ? microsoftRedirectUri
          : redirectUri,
      });
    }
  };

  const onAccessDenied = () => {
    const { setToastMsg, serviceType } = props;
    if (_.isEqual(serviceType, "GOOGLE")) {
      setToastMsg({
        msg: "toastMsgs:enable_dynamic_permission",
        locale_params: [
          {
            key: "permission",
            value: "google drive access",
            isPlainText: true,
          },
        ],
      });
    }
    if (_.isEqual(serviceType, "MICROSOFT")) {
      setToastMsg("toastMsgs:something_went_wrong");
    }
    if (onFailureCallBack) {
      onFailureCallBack();
    }
  };
  const { getAuthUrl } = _.get(SERVICES, serviceType, {});

  return (
    <React.Fragment>
      <OauthPopup
        url={getAuthUrl({ userType })}
        onCode={onAuthCallBack}
        onAccessDenied={onAccessDenied}
        ref={authRef}
        serviceType={serviceType}
        onFailureCallBack={onFailureCallBack}
      >
        {showChildren ? props.children : null}
      </OauthPopup>
      {isLoading && <FullScreenLoader />}
    </React.Fragment>
  );
};

const mapActionCreators = {
  getUserThirdPartyAuthorizedStatus,
  getUserThirdPartyPickerTokens,
  generateThirdPartyRefreshToken,
  setToastMsg,
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const userId = _.get(userInfo, "id");
  const userType = _.get(userInfo, "user_type");

  return {
    userId,
    userType,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators, null, {
    withRef: true,
  })
)(memo(forwardRef(ThirdPartyAuth)));
